import { ChecklistIcon, DuplicateIcon, EllipsisIcon, EmailIcon, ImageIcon, TrashIcon } from "@xola/icons";
import { flash, PopoverList } from "@xola/ui-kit";
import React from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { EmailTemplate, Merchandise } from "types";
import { api } from "@/api/api";
import { PromptModal } from "@/components/Common/PromptModal";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { PreviewMessageTemplate } from "@/components/Customers/Templates/PreviewMessageTemplate";
import { merchandiseLedgerPath } from "@/components/Reports/MerchandiseLedger/merchandiseLedgerRoute";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal, useVisibility } from "@/hooks/useVisibility";
import { MERCHANDISE_API_PATH, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";

export const MerchandiseActionButton = ({ merchandise }: { merchandise: Merchandise }) => {
    type ButtonClickEvent = React.MouseEvent<HTMLButtonElement>;
    const isMerchandiseDuplicateEnabled = false;
    const deleteModal = useModal();
    const [visible, { set: setVisible }] = useVisibility(false);
    const invalidate = useInvalidate();
    const history = useHistory();
    const [isEmailPreviewVisible, { toggle: toggleEmailPreview }] = useVisibility(false);

    const show = (e: ButtonClickEvent) => {
        e.stopPropagation();
        setVisible(true);
    };

    const hide = (e: ButtonClickEvent | { hide: () => void }) => {
        if ("hide" in e) {
            e.hide();
        } else {
            e.stopPropagation();
        }

        setVisible(false);
    };

    const { mutateAsync: deleteMerchandise, isLoading: isDeleting } = useMutation(async (id: string) => {
        return api.delete(`/${MERCHANDISE_API_PATH}/${id}`);
    });

    const { mutateAsync: duplicateMerchandise } = useMutation(async () => {
        const payload = {
            ...merchandise,
            id: undefined,
            name: `${merchandise.name} (Copy)`,
        };
        return api.post(`/${MERCHANDISE_API_PATH}`, payload);
    });

    const handleToggleDeleteModal = async (e: ButtonClickEvent) => {
        e.stopPropagation();
        deleteModal.toggle();
    };

    const invalidateProduct = () => {
        invalidate(MERCHANDISE_CACHE_KEY);
    };

    const handleDeleteConfirm = async () => {
        if (merchandise?.id) {
            await deleteMerchandise(merchandise.id);
            invalidateProduct();
            flash.success("Merchandise deleted");
        }

        deleteModal.hide();
    };

    const handleDuplicate = async (e: ButtonClickEvent) => {
        e.stopPropagation();
        await duplicateMerchandise();
        invalidateProduct();
        flash.success("Merchandise duplicated");
        setVisible(false);
    };

    const handlePreviewEmail = (e: ButtonClickEvent) => {
        e.stopPropagation();
        toggleEmailPreview();
        setVisible(false);
    };

    const handleManagePhotos = (e: ButtonClickEvent) => {
        e.stopPropagation();
        history.push(`/products/merchandise/${merchandise.id}/photos`);
        setVisible(false);
    };

    const handleOpenLedger = (e: ButtonClickEvent) => {
        e.stopPropagation();
        history.push(merchandiseLedgerPath({ productId: merchandise.id }));
        setVisible(false);
    };

    return (
        <>
            <PopoverList visible={visible} className="w-50 items-center" zIndex="10" onClickOutside={hide}>
                <SecondaryButton
                    size="small"
                    data-testid="merchandise-action-button-ellipsis"
                    onClick={visible ? hide : show}
                >
                    <EllipsisIcon />
                </SecondaryButton>

                {isMerchandiseDuplicateEnabled && (
                    <PopoverList.Item name="Duplicate" role="button" onClickItem={handleDuplicate}>
                        <DuplicateIcon />
                        <span>Duplicate</span>
                    </PopoverList.Item>
                )}

                <PopoverList.Item name="Preview Email" role="button" onClickItem={handlePreviewEmail}>
                    <EmailIcon />
                    <span>Preview Email</span>
                </PopoverList.Item>

                <PopoverList.Item name="Manage Photos" role="button" onClickItem={handleManagePhotos}>
                    <ImageIcon />
                    <span>Manage Photos</span>
                </PopoverList.Item>

                <PopoverList.Item name="Ledger" role="button" onClickItem={handleOpenLedger}>
                    <ChecklistIcon />
                    <span>Ledger</span>
                </PopoverList.Item>

                <PopoverList.Item name="Delete" role="button" onClickItem={handleToggleDeleteModal}>
                    <TrashIcon />
                    <span>Delete</span>
                </PopoverList.Item>
            </PopoverList>

            <PreviewMessageTemplate
                isOpen={isEmailPreviewVisible}
                payload={{
                    template: EmailTemplate.PURCHASE_CONFIRMATION,
                    ware: merchandise.id,
                }}
                onClose={toggleEmailPreview}
            />

            <PromptModal
                title="Delete Merchandise?"
                isLoading={isDeleting}
                isOpen={deleteModal.isVisible}
                onConfirm={handleDeleteConfirm}
                onClose={handleToggleDeleteModal}
            >
                Are you sure you want to delete this merchandise?
            </PromptModal>
        </>
    );
};
